<template>
  <div class="page-container">
    <Header title="Gestion des quotas de congés" />
    <div class="conges-container">
      <!-- En-tête avec actions -->
      <div class="conges-header">
        <h1 class="conges-title">Quotas de congés par agent</h1>
        <div class="conges-actions">
          <button class="btn btn-outline" @click="exportData">
            <span class="material-icons">file_download</span>
            Exporter
          </button>
          <button class="btn btn-primary" @click="openAddQuotaModal">
            <span class="material-icons">add</span>
            Nouveau quota
          </button>
        </div>
      </div>

      <!-- Filtres -->
      <div class="conges-filters">
        <div class="filters-grid">
          <div class="filter-group">
            <label class="filter-label">Agent</label>
            <select v-model="filters.personnelId" class="filter-input form-select">
              <option value="">Tous les agents</option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.nom }} {{ agent.prenom }}
              </option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">Type de congé</label>
            <select v-model="filters.typeCongeId" class="filter-input form-select">
              <option value="">Tous les types</option>
              <option v-for="type in typesConge" :key="type.id" :value="type.id">
                {{ type.libelle }}
              </option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label">Année</label>
            <select v-model="filters.annee" class="filter-input form-select">
              <option value="">Toutes les années</option>
              <option v-for="annee in annees" :key="annee" :value="annee">
                {{ annee }}
              </option>
            </select>
          </div>
        </div>
        <div class="filter-actions">
          <button @click="applyFilters" class="btn btn-primary">
            <span class="material-icons">search</span>
            Rechercher
          </button>
          <button @click="resetFilters" class="btn btn-outline">
            <span class="material-icons">refresh</span>
            Réinitialiser
          </button>
        </div>
      </div>

      <!-- Liste des quotas -->
      <div v-if="loading" class="loading-container">
        <div class="spinner"></div>
        <p>Chargement des quotas de congés...</p>
      </div>
      
      <div v-else-if="quotas.length === 0" class="empty-state">
        <span class="material-icons">event_busy</span>
        <h3>Aucun quota trouvé</h3>
        <p>Aucun quota ne correspond à vos critères de recherche ou aucun quota n'a été défini.</p>
        <button @click="openAddQuotaModal" class="btn btn-primary">
          Ajouter un quota
        </button>
      </div>
      
      <div v-else class="quotas-list">
        <table class="conges-table">
          <thead>
            <tr>
              <th>Agent</th>
              <th>Type de congé</th>
              <th>Année</th>
              <th>Quota total</th>
              <th>Utilisé</th>
              <th>Restant</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="quota in quotas" :key="quota.id">
              <td>
                {{ quota.personnel.nom }} {{ quota.personnel.prenom }}
                <div class="agent-details">{{ quota.personnel.fonction }}</div>
              </td>
              <td>
                <div class="conge-type" :style="{ backgroundColor: quota.typeConge.couleur + '20', color: quota.typeConge.couleur }">
                  {{ quota.typeConge.libelle }}
                </div>
              </td>
              <td>{{ quota.annee }}</td>
              <td>{{ quota.quotaTotal }} jours</td>
              <td>{{ quota.quotaUtilise }} jours</td>
              <td>
                <div :class="['quota-value', getQuotaClass(quota)]">
                  {{ quota.quotaRestant }} jours
                </div>
              </td>
              <td>
                <div class="conge-actions">
                  <button @click="editQuota(quota)" class="btn btn-icon btn-edit" title="Modifier">
                    <span class="material-icons">edit</span>
                  </button>
                  <button @click="confirmDeleteQuota(quota)" class="btn btn-icon btn-delete" title="Supprimer">
                    <span class="material-icons">delete</span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="quotas.length > 0" class="pagination">
        <div class="pagination-info">
          Affichage de {{ (pagination.page - 1) * pagination.limit + 1 }} à 
          {{ Math.min(pagination.page * pagination.limit, pagination.totalItems) }} 
          sur {{ pagination.totalItems }} quotas
        </div>
        <div class="pagination-controls">
          <button 
            class="pagination-button" 
            :class="{ 'pagination-disabled': pagination.page === 1 }"
            @click="goToPage(1)" 
            :disabled="pagination.page === 1"
          >
            <span class="material-icons">first_page</span>
          </button>
          <button 
            class="pagination-button" 
            :class="{ 'pagination-disabled': pagination.page === 1 }"
            @click="goToPage(pagination.page - 1)" 
            :disabled="pagination.page === 1"
          >
            <span class="material-icons">chevron_left</span>
          </button>
          
          <template v-for="pageNumber in displayedPages" :key="pageNumber">
            <button 
              class="pagination-button" 
              :class="{ 'pagination-active': pagination.page === pageNumber }"
              @click="goToPage(pageNumber)"
            >
              {{ pageNumber }}
            </button>
          </template>
          
          <button 
            class="pagination-button" 
            :class="{ 'pagination-disabled': pagination.page === pagination.totalPages }"
            @click="goToPage(pagination.page + 1)" 
            :disabled="pagination.page === pagination.totalPages"
          >
            <span class="material-icons">chevron_right</span>
          </button>
          <button 
            class="pagination-button" 
            :class="{ 'pagination-disabled': pagination.page === pagination.totalPages }"
            @click="goToPage(pagination.totalPages)" 
            :disabled="pagination.page === pagination.totalPages"
          >
            <span class="material-icons">last_page</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal d'ajout/modification de quota -->
    <dialog ref="quotaModal" class="quota-modal">
      <div class="modal-header">
        <h3>{{ isEditMode ? 'Modifier le quota' : 'Ajouter un quota' }}</h3>
        <button @click="closeModal" class="btn-close">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="saveQuota">
          <div class="form-group">
            <label class="form-label">Agent *</label>
            <select v-model="formData.personnelId" class="form-select" :class="{ 'form-error': errors.personnelId }" required>
              <option value="">Sélectionner un agent</option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                {{ agent.nom }} {{ agent.prenom }} ({{ agent.fonction }})
              </option>
            </select>
            <div v-if="errors.personnelId" class="form-error">{{ errors.personnelId }}</div>
          </div>
          
          <div class="form-group">
            <label class="form-label">Type de congé *</label>
            <select v-model="formData.typeCongeId" class="form-select" :class="{ 'form-error': errors.typeCongeId }" required>
              <option value="">Sélectionner un type</option>
              <option v-for="type in typesConge" :key="type.id" :value="type.id">
                {{ type.libelle }}
              </option>
            </select>
            <div v-if="errors.typeCongeId" class="form-error">{{ errors.typeCongeId }}</div>
          </div>
          
          <div class="form-group">
            <label class="form-label">Année *</label>
            <select v-model="formData.annee" class="form-select" :class="{ 'form-error': errors.annee }" required>
              <option value="">Sélectionner une année</option>
              <option v-for="annee in annees" :key="annee" :value="annee">
                {{ annee }}
              </option>
            </select>
            <div v-if="errors.annee" class="form-error">{{ errors.annee }}</div>
          </div>
          
          <div class="form-group">
            <label class="form-label">Quota total (jours) *</label>
            <input 
              type="number" 
              v-model.number="formData.quotaTotal" 
              class="form-input" 
              :class="{ 'form-error': errors.quotaTotal }" 
              min="0" 
              step="0.5" 
              required
            />
            <div v-if="errors.quotaTotal" class="form-error">{{ errors.quotaTotal }}</div>
          </div>
          
          <div class="form-group">
            <label class="form-label">Commentaire</label>
            <textarea 
              v-model="formData.commentaire" 
              class="form-textarea" 
              rows="3" 
              placeholder="Commentaire optionnel"
            ></textarea>
          </div>
          
          <div class="form-actions">
            <button @click="closeModal" class="btn btn-outline">Annuler</button>
            <button @click="saveQuota" class="btn btn-primary" :disabled="submitting">
              <span v-if="submitting" class="spinner-small"></span>
              {{ isEditMode ? 'Mettre à jour' : 'Ajouter' }}
            </button>
          </div>
        </form>
      </div>
    </dialog>

    <!-- Modal de confirmation de suppression -->
    <dialog ref="confirmDeleteModal" class="confirm-modal">
      <div class="modal-header">
        <h3>Confirmer la suppression</h3>
        <button @click="closeConfirmModal" class="btn-close">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body">
        <p>Êtes-vous sûr de vouloir supprimer ce quota de congé ?</p>
        <p v-if="quotaToDelete">
          Agent: <strong>{{ quotaToDelete.personnel?.nom }} {{ quotaToDelete.personnel?.prenom }}</strong><br>
          Type: <strong>{{ quotaToDelete.typeConge?.libelle }}</strong><br>
          Année: <strong>{{ quotaToDelete.annee }}</strong>
        </p>
        <p class="warning">Cette action est irréversible et peut affecter les demandes de congés existantes.</p>
      </div>
      <div class="modal-footer">
        <button @click="closeConfirmModal" class="btn btn-outline">Annuler</button>
        <button @click="deleteQuota" class="btn btn-danger">Supprimer</button>
      </div>
    </dialog>

    <!-- Notification Toast -->
    <div v-if="notification.show" class="notification" :class="notification.type">
      <div class="notification-content">
        <span class="material-icons">{{ getNotificationIcon(notification.type) }}</span>
        <div>
          <h4>{{ notification.title }}</h4>
          <p>{{ notification.message }}</p>
        </div>
      </div>
      <button class="notification-close" @click="notification.show = false">
        <span class="material-icons">close</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useQuotas } from '@/composables/useQuotas';
import api from '@/services/api';
import Header from '@/components/Header.vue';
import ToastNotification from '@/components/ToastNotification.vue';

// État local
const loading = ref(true);
const submitting = ref(false);
const isEditMode = ref(false);
const quotas = ref([]);
const agents = ref([]);
const typesConge = ref([]);
const quotaModal = ref(null);
const confirmDeleteModal = ref(null);
const quotaToDelete = ref(null);

// Années disponibles (année courante - 1 jusqu'à année courante + 2)
const currentYear = new Date().getFullYear();
const annees = ref([
  currentYear - 1,
  currentYear,
  currentYear + 1,
  currentYear + 2
]);

// Pagination
const pagination = reactive({
  page: 1,
  limit: 10,
  totalItems: 0,
  totalPages: 1
});

// Filtres
const filters = reactive({
  personnelId: '',
  typeCongeId: '',
  annee: currentYear
});

// Formulaire
const formData = reactive({
  id: null,
  personnelId: '',
  typeCongeId: '',
  annee: currentYear,
  quotaTotal: 0,
  commentaire: ''
});

// Erreurs du formulaire
const errors = reactive({
  personnelId: '',
  typeCongeId: '',
  annee: '',
  quotaTotal: ''
});

// Notification
const notification = reactive({
  show: false,
  type: 'success',
  title: '',
  message: '',
});

// Pagination calculée
const displayedPages = computed(() => {
  const totalPages = pagination.totalPages;
  const currentPage = pagination.page;
  
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  
  if (currentPage <= 3) {
    return [1, 2, 3, 4, 5];
  }
  
  if (currentPage >= totalPages - 2) {
    return [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }
  
  return [currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2];
});

// Méthodes
const loadQuotas = async () => {
  loading.value = true;
  
  try {
    const { getQuotas } = useQuotas();
    const result = await getQuotas({
      page: pagination.page,
      limit: pagination.limit,
      ...filters
    });
    
    quotas.value = result.items;
    pagination.totalItems = result.totalItems;
    pagination.totalPages = result.totalPages;
  } catch (error) {
    console.error('Erreur lors du chargement des quotas:', error);
    showNotification('error', 'Erreur', 'Impossible de charger les quotas de congés');
  } finally {
    loading.value = false;
  }
};

const loadAgents = async () => {
  try {
    const response = await api.getPersonnelList(1, 1000); // Charger tous les agents
    agents.value = response.data.data;
  } catch (error) {
    console.error('Erreur lors du chargement des agents:', error);
    showNotification('error', 'Erreur', 'Impossible de charger la liste des agents');
  }
};

const loadTypesConge = async () => {
  try {
    const response = await api.getCongesTypes();
    typesConge.value = response.data.data;
  } catch (error) {
    console.error('Erreur lors du chargement des types de congé:', error);
    showNotification('error', 'Erreur', 'Impossible de charger les types de congé');
  }
};

const applyFilters = () => {
  pagination.page = 1; // Revenir à la première page
  loadQuotas();
};

const resetFilters = () => {
  filters.personnelId = '';
  filters.typeCongeId = '';
  filters.annee = currentYear;
  pagination.page = 1;
  loadQuotas();
};

const goToPage = (page) => {
  pagination.page = page;
  loadQuotas();
};

const getQuotaClass = (quota) => {
  const ratio = quota.quotaRestant / quota.quotaTotal;
  
  if (ratio <= 0) return 'quota-empty';
  if (ratio < 0.25) return 'quota-low';
  if (ratio < 0.5) return 'quota-medium';
  return 'quota-high';
};

const openAddQuotaModal = () => {
  isEditMode.value = false;
  resetForm();
  quotaModal.value.showModal();
};

const editQuota = (quota) => {
  isEditMode.value = true;
  
  formData.id = quota.id;
  formData.personnelId = quota.personnel.id;
  formData.typeCongeId = quota.typeConge.id;
  formData.annee = quota.annee;
  formData.quotaTotal = quota.quotaTotal;
  formData.commentaire = quota.commentaire || '';
  
  quotaModal.value.showModal();
};

const closeModal = () => {
  quotaModal.value.close();
  resetForm();
};

const resetForm = () => {
  formData.id = null;
  formData.personnelId = '';
  formData.typeCongeId = '';
  formData.annee = currentYear;
  formData.quotaTotal = 0;
  formData.commentaire = '';
  
  // Réinitialiser les erreurs
  Object.keys(errors).forEach(key => {
    errors[key] = '';
  });
};

const validateForm = () => {
  let isValid = true;
  
  // Réinitialiser les erreurs
  Object.keys(errors).forEach(key => {
    errors[key] = '';
  });
  
  // Validation de l'agent
  if (!formData.personnelId) {
    errors.personnelId = 'Veuillez sélectionner un agent';
    isValid = false;
  }
  
  // Validation du type de congé
  if (!formData.typeCongeId) {
    errors.typeCongeId = 'Veuillez sélectionner un type de congé';
    isValid = false;
  }
  
  // Validation de l'année
  if (!formData.annee) {
    errors.annee = 'Veuillez sélectionner une année';
    isValid = false;
  }
  
  // Validation du quota total
  if (formData.quotaTotal < 0) {
    errors.quotaTotal = 'Le quota ne peut pas être négatif';
    isValid = false;
  }
  
  return isValid;
};

const saveQuota = async () => {
  if (!validateForm()) {
    showNotification('error', 'Formulaire incomplet', 'Veuillez corriger les erreurs dans le formulaire');
    return;
  }
  
  submitting.value = true;
  
  try {
    const { createQuota, updateQuota } = useQuotas();
    
    if (isEditMode.value) {
      await updateQuota(formData.id, {
        personnelId: formData.personnelId,
        typeCongeId: formData.typeCongeId,
        annee: formData.annee,
        quotaTotal: formData.quotaTotal,
        commentaire: formData.commentaire
      });
      
      showNotification('success', 'Quota mis à jour', 'Le quota de congé a été mis à jour avec succès');
    } else {
      await createQuota({
        personnelId: formData.personnelId,
        typeCongeId: formData.typeCongeId,
        annee: formData.annee,
        quotaTotal: formData.quotaTotal,
        commentaire: formData.commentaire
      });
      
      showNotification('success', 'Quota ajouté', 'Le quota de congé a été ajouté avec succès');
    }
    
    closeModal();
    loadQuotas();
  } catch (error) {
    console.error('Erreur lors de l\'enregistrement du quota:', error);
    showNotification(
      'error',
      'Erreur',
      isEditMode.value ? 'Impossible de mettre à jour le quota' : 'Impossible d\'ajouter le quota'
    );
  } finally {
    submitting.value = false;
  }
};

const confirmDeleteQuota = (quota) => {
  quotaToDelete.value = quota;
  confirmDeleteModal.value.showModal();
};

const closeConfirmModal = () => {
  confirmDeleteModal.value.close();
  quotaToDelete.value = null;
};

const deleteQuota = async () => {
  if (!quotaToDelete.value) return;
  
  try {
    const { deleteQuota } = useQuotas();
    await deleteQuota(quotaToDelete.value.id);
    
    showNotification('success', 'Quota supprimé', 'Le quota de congé a été supprimé avec succès');
    closeConfirmModal();
    loadQuotas();
  } catch (error) {
    console.error('Erreur lors de la suppression du quota:', error);
    showNotification('error', 'Erreur', 'Impossible de supprimer le quota de congé');
  }
};

const exportData = async () => {
  try {
    // Implémenter l'export des données
    showNotification('info', 'Export en cours', 'Préparation de l\'export des quotas...');
    
    // Simuler un délai pour l'export
    setTimeout(() => {
      showNotification('success', 'Export réussi', 'Les données ont été exportées avec succès');
    }, 1500);
  } catch (error) {
    console.error('Erreur lors de l\'export:', error);
    showNotification('error', 'Erreur', 'Impossible d\'exporter les données');
  }
};

const showNotification = (type, title, message) => {
  notification.type = type;
  notification.title = title;
  notification.message = message;
  notification.show = true;
  
  // Masquer automatiquement après 5 secondes
  setTimeout(() => {
    notification.show = false;
  }, 5000);
};

const getNotificationIcon = (type) => {
  switch (type) {
    case 'success': return 'check_circle';
    case 'error': return 'error';
    case 'warning': return 'warning';
    case 'info': return 'info';
    default: return 'info';
  }
};

// Hooks de cycle de vie
onMounted(async () => {
  try {
    await Promise.all([
      loadAgents(),
      loadTypesConge()
    ]);
    
    await loadQuotas();
  } catch (error) {
    console.error('Erreur lors du chargement initial:', error);
  }
});
</script>

<style scoped>
@import '@/assets/css/variables.css';
@import '@/assets/css/conges.css';

/* Styles spécifiques à cette vue */
.agent-details {
  font-size: 12px;
  color: #666;
  margin-top: 2px;
}

.form-input,
.form-select,
.form-textarea {
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #ddd;
  font-size: 14px;
  transition: all 0.2s;
  width: 100%;
  box-sizing: border-box;
}

.quota-value {
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
}

.quota-high {
  background-color: var(--turquoise-pale);
  color: var(--turquoise-deep);
}

.quota-medium {
  background-color: #fff3cd;
  color: #856404;
}

.quota-low {
  background-color: #ffeae7;
  color: var(--coral-soft);
}

.quota-empty {
  background-color: #ffded9;
  color: #ff6a56;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(48, 199, 181, 0.1);
  border-radius: 50%;
  border-top-color: var(--turquoise-primary);
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.spinner-small {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
  margin-right: 8px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  text-align: center;
}

.empty-state .material-icons {
  font-size: 48px;
  color: #ccc;
  margin-bottom: 16px;
}

.empty-state h3 {
  margin: 0 0 8px;
  color: #333;
}

.empty-state p {
  margin: 0 0 24px;
  color: #666;
  max-width: 400px;
}

.quota-modal {
  border: none;
  border-radius: 12px;
  padding: 0;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25), 0 5px 15px rgba(0, 0, 0, 0.15);
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
  animation: fadeIn 0.3s ease-out;
  overflow: hidden;
}

.quota-modal::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
}

.quota-modal::backdrop {
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 20px;
  background: linear-gradient(to right, var(--turquoise-deep), var(--turquoise-primary));
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
}

.btn-close {
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-icon:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.btn-icon:active {
  transform: translateY(1px);
}

.btn-icon .material-icons {
  font-size: 18px;
  color: #666;
}

.btn-edit .material-icons {
  color: var(--turquoise-dark);
}

.btn-delete .material-icons {
  color: var(--coral-soft);
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  background-color: #f8f8f8;
  border-top: 1px solid #eee;
}

.warning {
  color: var(--coral-soft);
  font-weight: 500;
}

.pagination-active {
  background-color: var(--turquoise-primary);
  color: white;
  border-color: var(--turquoise-primary);
  box-shadow: 0 2px 4px rgba(48, 199, 181, 0.2);
}

.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 320px;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  z-index: 1000;
  animation: slideIn 0.3s ease-out;
  overflow: hidden;
}

.notification-content {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.notification h4 {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
}

.notification p {
  margin: 0;
  font-size: 14px;
}

.notification-close {
  background: transparent;
  border: none;
  color: inherit;
  opacity: 0.7;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-close:hover {
  opacity: 1;
}

.notification.success {
  background-color: var(--turquoise-pale);
  color: var(--turquoise-deep);
  border-left: 4px solid var(--turquoise-primary);
}

.notification.error {
  background-color: #ffeae7;
  color: #d63031;
  border-left: 4px solid var(--coral-soft);
}

.notification.warning {
  background-color: #fff3cd;
  color: #856404;
  border-left: 4px solid #ffc107;
}

.notification.info {
  background-color: #e6f7ff;
  color: #0c5460;
  border-left: 4px solid var(--blue-marine);
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  border-color: var(--turquoise-primary);
  outline: none;
  box-shadow: 0 0 0 2px rgba(48, 199, 181, 0.2);
}

.form-input.form-error,
.form-select.form-error,
.form-textarea.form-error {
  border-color: var(--coral-soft);
  background-color: #fff6f5;
}

.form-input.form-error:focus,
.form-select.form-error:focus,
.form-textarea.form-error:focus {
  box-shadow: 0 0 0 2px rgba(255, 122, 104, 0.2);
}

.btn {
  border: none;
  border-radius: 6px;
  padding: 10px 16px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s, transform 0.1s;
}

.btn-primary {
  background-color: var(--turquoise-primary);
  color: white;
}

.btn-primary:hover {
  background-color: var(--turquoise-dark);
}

.btn-primary:active {
  transform: translateY(1px);
}

.btn-outline {
  background-color: transparent;
  color: var(--turquoise-primary);
  border: 1px solid var(--turquoise-primary);
  border-radius: 6px;
  padding: 10px 16px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s, transform 0.1s;
}

.btn-outline:hover {
  background-color: var(--turquoise-pale);
}

.btn-outline:active {
  transform: translateY(1px);
}

.btn-danger {
  background-color: var(--coral-soft);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s;
}

.btn-danger:hover {
  background-color: #ff6a56;
}

.btn-danger:active {
  transform: translateY(1px);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}
</style>
